import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';

import {
  DescriptionBox,
  TextQuestion,
  SectionHeader,
  NumberQuestion,
  DateQuestionInternal as DateQuestion,
  DropdownQuestion,
  RadioCheckboxQuestion,
  MatrixQuestion,
  SliderQuestion,
  DangerousHTMLDiv,
} from 'ecs-react-components/dist/index.es';
import * as QUESTIONNAIRE_ITEM_TYPES from '../../redux/modules/questionnaires/questionnaire-item-types';
import {
  loadQuestionnaireDetails,
  submitQuestionnaire,
  loadNewQuestionnairesCount,
  validateQuestionnaire,
} from '../../redux/modules/questionnaires/questionnaires-middleware';
import { questionnaireAnswerUpdated, questionnaireAnswerCleared }
  from '../../redux/modules/questionnaires/questionnaires-action-creator';
import { setIsDedicatedWorkflow } from '../../redux/modules/workflow/workflow-action-creator';
import { messages } from './Questionnaires.i18n';
import * as MsgBarActions from '../../redux/modules/system-shared/message-bar-action-creator';
import Message from '../../containers/Message/Message';
import LoadingIndicatorContainer from '../LoadingIndicatorContainer';
import { callWithLoadingIndicator } from '../../redux/modules/loading-indicator/loading-indicator-middleware';

class CompleteQuestionnaire extends React.Component {
  componentDidMount = () => {
    this.props.dispatch(callWithLoadingIndicator(
      'CompleteQuestionnaire',
      () => this.props.dispatch(loadQuestionnaireDetails(this.props.patientId, this.props.selectedQuestionnaireId))));
    this.props.dispatch(setIsDedicatedWorkflow(true));
  }

  componentWillUnmount = () => {
    this.props.dispatch(setIsDedicatedWorkflow(false));
  }

  onSubmit = () => {
    this.validateQuestionnaire().then(
      (isValid) => {
        if (isValid) {
          this.props.dispatch(
            callWithLoadingIndicator('CompleteQuestionnaire',
              () => this.props.dispatch(submitQuestionnaire(this.props.patientId, this.props.questionnaire))))
            .then(
              () => {
                if (this.props.onSubmit) {
                  this.props.onSubmit();
                }
                this.props.history.push(this.props.questionnaireScoringPage);
                this.props.dispatch(loadNewQuestionnairesCount(this.props.patientId));
                this.props.dispatch(questionnaireAnswerCleared(this.props.questionnaire.assignedQuestionnaireId));
              });
        } else {
          this.props.dispatch(MsgBarActions.showErrorMessage('questionnairevalidatefailed'));
          window.scrollTo(0, 0);
        }
      },
      () => this.props.dispatch(MsgBarActions.showErrorMessage()));
  }

  onClickCancel = (event) => {
    event.preventDefault();
    this.props.history.push(this.props.questionnairesOrigin);
  }

  getPatientInstructionsBoxContent = () => {
    const { questionnaire } = this.props;
    const patientInstructionsId = 'questionnaire_template_patientInstructions_' + questionnaire.assignedQuestionnaireId;
    const defaultMessageId = 'questionnaire_default_message_' + questionnaire.assignedQuestionnaireId;
    const asteriskClassName = 'fa-solid fa-asterisk color-required';

    return (
      <div>
        <div className="paragraph">
          <DangerousHTMLDiv qaTag={ patientInstructionsId } html={questionnaire.patientInstructions} />
        </div>
        <p data-qa-item={ defaultMessageId } className="required-question-text mb-1">
          <FormattedMessage { ...messages.asterisk.name } />(<span className={ asteriskClassName }></span>)
          <FormattedMessage { ...messages.asterisk.description } />
        </p>
      </div>);
  };

  getItemsWithMatrixGrouped = (questionnaireItems) => {
    const newItems = [];
    questionnaireItems
      .sort((item1, item2) => item1.sortOrder - item2.sortOrder)
      .map((item) => {
        if (item.questionnaireItemType === QUESTIONNAIRE_ITEM_TYPES.MATRIX_GROUPING &&
            newItems.find((x) => x.questionMatrixGuid === item.questionMatrixGuid) === undefined) {
          newItems.push({
            questionnaireItemType: QUESTIONNAIRE_ITEM_TYPES.MATRIX_GROUPING,
            questionMatrixGuid: item.questionMatrixGuid,
            questionnaireItems:
              questionnaireItems.filter((x) => x.questionMatrixGuid === item.questionMatrixGuid)
                .map((x) => ({ questionnaireItem: x })),
          });
        } else if (item.questionnaireItemType !== QUESTIONNAIRE_ITEM_TYPES.MATRIX_GROUPING) {
          newItems.push(item);
        }
        return item;
      });

    return newItems;
  };

  validateQuestionnaire = () => (
    this.props.dispatch(
      validateQuestionnaire(this.props.questionnaire.assignedQuestionnaireId))
  );

  validateQuestionnaireOnChange = () => (
    this.validateQuestionnaire().then(
      (isValid) => {
        if (isValid) {
          this.props.dispatch(MsgBarActions.dismissMessage());
        }
      },
      () => this.props.dispatch(MsgBarActions.showErrorMessage()))
  )

  updateQuestionnaireAnswer = (questionnaireItemId, value) => {
    this.props.dispatch(
      questionnaireAnswerUpdated(
        this.props.questionnaire.assignedQuestionnaireId, questionnaireItemId, value));
  }

  renderQuestionnaireTitle(title, id) {
    return (
    <div className="row mb-3">
      <div className="col-auto">
        <h1 data-qa-item={id} className="float-left mr-2">{title}</h1>
      </div>
    </div>);
  }

  render() {
    const { questionnaire } = this.props;
    const { formatMessage } = this.props.intl;
    if (questionnaire === undefined || questionnaire === null) {
      return null;
    }

    const questionnaireTitleId = 'questionnaire_title_' + questionnaire.assignedQuestionnaireId;

    return (
      <LoadingIndicatorContainer>
      <div>
        <form>
          <Message />
          {this.renderQuestionnaireTitle(questionnaire.templateTitle, questionnaireTitleId)}
          <DescriptionBox content={ this.getPatientInstructionsBoxContent() } showPrintButton={ false } />
                {
                  this.getItemsWithMatrixGrouped(questionnaire.questionnaireItems)
                    .map((item) => {
                      let itemToRender;
                      switch (item.questionnaireItemType) {
                        case QUESTIONNAIRE_ITEM_TYPES.TEXT_QUESTION:
                          itemToRender = (
                            <TextQuestion
                              questionnaireItem={ item }
                              updateAnswer={ this.updateQuestionnaireAnswer }
                              key={ item.questionnaireItemId }
                              validateQuestionnaire={ this.validateQuestionnaireOnChange }
                            />);
                          break;
                        case QUESTIONNAIRE_ITEM_TYPES.SECTION_HEADER:
                          itemToRender = (
                            <SectionHeader
                              id={ item.questionnaireItemId }
                              text={ item.formattedText }
                              key={ item.questionnaireItemId }
                            />);
                          break;
                        case QUESTIONNAIRE_ITEM_TYPES.BOOLEAN_QUESTION:
                        case QUESTIONNAIRE_ITEM_TYPES.RADIOBUTTON_QUESTION:
                          itemToRender = (
                            <RadioCheckboxQuestion
                              questionnaireItem={ item }
                              updateAnswer={ this.updateQuestionnaireAnswer }
                              key={ item.questionnaireItemId }
                              inputType="radio"
                              validateQuestionnaire={ this.validateQuestionnaireOnChange }
                            />
                          );
                          break;
                        case QUESTIONNAIRE_ITEM_TYPES.CHECKBOX_QUESTION:
                          itemToRender = (
                            <RadioCheckboxQuestion
                              questionnaireItem={ item }
                              updateAnswer={ this.updateQuestionnaireAnswer }
                              key={ item.questionnaireItemId }
                              inputType="checkbox"
                              validateQuestionnaire={ this.validateQuestionnaireOnChange }
                            />
                          );
                          break;
                        case QUESTIONNAIRE_ITEM_TYPES.DROPDOWN_QUESTION:
                          itemToRender = (
                            <DropdownQuestion
                              questionnaireItem={ item }
                              updateAnswer={ this.updateQuestionnaireAnswer }
                              key={ item.questionnaireItemId }
                              validateQuestionnaire={ this.validateQuestionnaireOnChange }
                            />
                          );
                          break;
                        case QUESTIONNAIRE_ITEM_TYPES.NUMBER_QUESTION:
                          itemToRender = (
                            <NumberQuestion
                              questionnaireItem={ item }
                              updateAnswer={ this.updateQuestionnaireAnswer }
                              key={ item.questionnaireItemId }
                              validateQuestionnaire={ this.validateQuestionnaireOnChange }
                            />
                          );
                          break;
                        case QUESTIONNAIRE_ITEM_TYPES.DATE_QUESTION:
                          itemToRender = (
                            <DateQuestion
                              questionnaireItem={ item }
                              updateAnswer={ this.updateQuestionnaireAnswer }
                              key={ item.questionnaireItemId }
                              language={ this.props.language }
                              validateQuestionnaire={ this.validateQuestionnaireOnChange }
                              dateQuestionLabel={ formatMessage(messages.labels.dateDropdown) }
                            />
                          );
                          break;
                        case QUESTIONNAIRE_ITEM_TYPES.SLIDER_QUESTION:
                          itemToRender = (
                            <SliderQuestion
                              questionnaireItem={ item }
                              updateAnswer={ this.updateQuestionnaireAnswer }
                              key={ item.questionnaireItemId }
                              validateQuestionnaire={ this.validateQuestionnaireOnChange }
                            />
                          );
                          break;
                        case QUESTIONNAIRE_ITEM_TYPES.MATRIX_GROUPING:
                          itemToRender = (
                            <MatrixQuestion
                              questionnaireItems={ item.questionnaireItems }
                              updateAnswer={ this.updateQuestionnaireAnswer }
                              key={ item.questionMatrixGuid }
                              validateQuestionnaire={ this.validateQuestionnaireOnChange }
                            />
                          );
                          break;
                        default:
                          itemToRender = null;
                      }
                      return itemToRender;
                    })
                }
        </form>
        <div className="control-buttons center">
          <button data-qa-item="submit_questionnaire" className="btn btn-primary mr-1" onClick={this.onSubmit}>
            <FormattedMessage { ...messages.button.submit } />
          </button>
          <button data-qa-item="cancel_questionnaire" className="btn btn-link" onClick={this.onClickCancel}>
            <FormattedMessage { ...messages.button.cancel } />
          </button>
        </div>
      </div>
      </LoadingIndicatorContainer>
    );
  }
}

CompleteQuestionnaire.propTypes = {
  dispatch: PropTypes.func,
  patientId: PropTypes.string,
  questionnaire: PropTypes.object,
  onSubmit: PropTypes.func,
  language: PropTypes.string,
  selectedQuestionnaireId: PropTypes.string,
  intl: PropTypes.object,
  questionnairesOrigin: PropTypes.string,
  questionnaireScoringPage: PropTypes.string,
  history: PropTypes.object,
};

const mapStateToProps = (state, ownProps) => ({
  questionnaire: ownProps.questionnaire || state.questionnaires
    .assignedQuestionnaires
    .find((q) => q.assignedQuestionnaireId === state.questionnaires.selectedQuestionnaireId),
  patientId: state.profile.patient.patientId,
  language: state.language.languageCode,
  selectedQuestionnaireId: ownProps.selectedQuestionnaireId || state.questionnaires.selectedQuestionnaireId,
  questionnairesOrigin: state.questionnaires.questionnairesOrigin,
  questionnaireScoringPage: state.questionnaires.questionnaireScoringPage,
});

export default connect(mapStateToProps)(injectIntl(CompleteQuestionnaire));
